/** @fileoverview Renders the Fabrik app list and create-app entry point. */

import { useMemo, useState } from 'react';
import { ArrowUpDown, CircleAlert, Layers3, Plus, Search } from 'lucide-react';
import type { Tenant, User } from '../types/identity';
import useConnectedSystems from '../hooks/useConnectedSystems';
import AppCard from './Apps/AppCard';
import DeleteAppDialog from './Apps/DeleteAppDialog';
import { ErrorIllustration } from './illustrations/ErrorIllustration';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import useFabrikApps from '../hooks/useFabrikApps';

type Props = {
  tenant: Tenant;
  user?: User;
  onCreate: () => void;
  onOpenApp: (appId: string) => void;
};

type AppFilter = 'all' | 'published' | 'drafts';

const isPublished = (app: {
  publishedVersion?: unknown;
  production_app_version_id?: string | null;
}) => Boolean(app.publishedVersion ?? app.production_app_version_id);

export default function FabrikHome({ tenant, user, onCreate, onOpenApp }: Props) {
  const systems = useConnectedSystems(tenant.tenant_id);
  const {
    apps,
    loading,
    error,
    deleteTarget,
    setDeleteTarget,
    deletingAppId,
    deleteError,
    setDeleteError,
    retryLoad,
    duplicatingAppId,
    duplicateApp,
    deleteApp,
  } = useFabrikApps(tenant.tenant_id);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<AppFilter>('all');
  const [sort, setSort] = useState<'recent' | 'name'>('recent');

  const appLoadFailed = Boolean(error && apps.length === 0);
  const counts = {
    all: apps.length,
    published: apps.filter(isPublished).length,
    drafts: apps.filter((app) => !isPublished(app)).length,
  };
  const visibleApps = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return apps
      .filter((app) =>
        filter === 'all' ? true : filter === 'published' ? isPublished(app) : !isPublished(app)
      )
      .filter(
        (app) =>
          !normalizedQuery ||
          `${app.name} ${app.description ?? ''}`.toLowerCase().includes(normalizedQuery)
      )
      .sort((a, b) =>
        sort === 'name'
          ? a.name.localeCompare(b.name)
          : Date.parse(b.updated_at ?? '') - Date.parse(a.updated_at ?? '')
      );
  }, [apps, filter, query, sort]);

  if (appLoadFailed) {
    return (
      <main className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-app-surface-muted px-5 py-10 sm:px-8">
        <section className="mx-auto grid w-full max-w-5xl items-center gap-8 md:grid-cols-2 md:gap-12">
          <ErrorIllustration
            className="mx-auto h-auto max-h-96 w-full max-w-lg"
            aria-hidden="true"
          />
          <div className="mx-auto max-w-lg text-center md:text-left">
            <p className="text-xs font-semibold uppercase tracking-widest text-app-danger">
              Fabrik service issue
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-app-text">
              We couldn’t load your apps
            </h1>
            <p className="mt-4 text-base leading-7 text-app-text-muted">{error}</p>
            <div className="mt-6 flex justify-center md:justify-start">
              <Button
                type="button"
                className="h-10 rounded-lg border-app-action bg-app-action px-5 text-sm font-semibold text-app-action-foreground hover:bg-app-action-hover"
                onClick={retryLoad}
              >
                Try again
              </Button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-app-surface-muted">
      <div className="mx-auto w-full max-w-360 px-5 pb-12 pt-7 sm:px-8 xl:px-16">
        <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="m-0 text-3xl font-bold tracking-tight text-app-text">Your apps</h1>
              {(!loading || apps.length > 0) && !appLoadFailed && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-app-text">
                  <Layers3 className="h-4 w-4 text-app-branch" aria-hidden="true" />
                  {apps.length} apps
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-app-text-muted">
              Build, manage, and share apps across your connected systems.
            </p>
          </div>
          <Button
            type="button"
            className="h-10 rounded-lg border-app-action bg-app-action px-4 text-sm font-semibold text-app-action-foreground hover:bg-app-action-hover"
            onClick={onCreate}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create app
          </Button>
        </header>

        {error && (
          <Alert className="mb-5 border-app-danger-border bg-app-danger-surface text-app-danger">
            <CircleAlert aria-hidden="true" />
            <div className="min-w-0">
              <AlertTitle className="font-semibold text-app-danger">
                Could not complete the request
              </AlertTitle>
              <AlertDescription className="mt-1 text-sm text-app-danger">{error}</AlertDescription>
            </div>
          </Alert>
        )}

        {apps.length > 0 && (
          <section aria-label="Filter apps" className="mb-4 flex flex-wrap items-center gap-3">
            <label className="relative min-w-56 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search apps by name or description…"
                aria-label="Search apps by name or description"
                className="h-10 w-full rounded-lg border border-app-border bg-app-surface-card py-2 pl-10 pr-3 text-sm text-app-text shadow-sm placeholder:text-app-text-subtle focus-visible:border-app-branch focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-branch/20"
              />
            </label>
            <div
              className="inline-flex h-10 items-center rounded-lg border border-app-border bg-app-surface-card p-1"
              role="group"
              aria-label="App status"
            >
              {(
                [
                  ['all', 'All apps'],
                  ['published', 'Published'],
                  ['drafts', 'Drafts'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                  className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-medium transition-colors ${
                    filter === value
                      ? 'bg-app-branch-subtle text-app-text'
                      : 'text-app-text-muted hover:bg-app-surface-muted'
                  }`}
                >
                  {label}
                  <span className="rounded-full bg-app-surface-muted-strong px-1.5 py-0.5 text-xs leading-none text-app-text-muted">
                    {counts[value]}
                  </span>
                </button>
              ))}
            </div>
            <Select
              value={sort}
              onValueChange={(value) => {
                if (value === 'recent' || value === 'name') setSort(value);
              }}
            >
              <SelectTrigger
                aria-label="Sort apps"
                className="h-10 min-w-44 rounded-lg border-app-border bg-app-surface-card px-3 text-sm text-app-text shadow-sm focus-visible:border-app-branch focus-visible:ring-app-branch/20"
              >
                <ArrowUpDown className="text-app-text-muted" aria-hidden="true" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start" alignItemWithTrigger={false}>
                <SelectGroup>
                  <SelectItem value="recent">Recently edited</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </section>
        )}

        <div
          className="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
          aria-busy={loading}
        >
          {loading && (
            <>
              <span className="sr-only">Loading your apps…</span>
              {Array.from({ length: 6 }, (_, index) => (
                <div
                  key={index}
                  className="h-44 animate-pulse rounded-xl border border-app-border bg-app-surface-card"
                  aria-hidden="true"
                />
              ))}
            </>
          )}
          {!loading &&
            visibleApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                tenantName={tenant.name}
                systems={systems}
                user={user}
                duplicatingAppId={duplicatingAppId}
                onDuplicate={() => void duplicateApp(app)}
                onDelete={() => {
                  setDeleteTarget(app);
                  setDeleteError('');
                }}
                onOpen={() => onOpenApp(app.id)}
              />
            ))}
          {!loading && !appLoadFailed && visibleApps.length === 0 && (
            <div className="col-span-full flex min-h-64 flex-col items-center justify-center rounded-xl border border-app-border bg-app-surface-card px-6 py-12 text-center shadow-sm">
              {apps.length === 0 && (
                <img
                  src="/images/no-apps-illustration.png"
                  alt=""
                  className="mb-3 h-auto w-full max-w-48 object-contain"
                />
              )}
              <h2 className="text-base font-semibold text-app-text">
                {apps.length ? 'No matching apps' : 'No apps yet'}
              </h2>
              <p className="mt-2 max-w-md text-sm text-app-text-muted">
                {apps.length
                  ? 'Try changing your search or status filter.'
                  : 'Create your first app to start building across your connected systems.'}
              </p>
              {apps.length ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-5"
                  onClick={() => {
                    setQuery('');
                    setFilter('all');
                  }}
                >
                  Clear search and filters
                </Button>
              ) : (
                <Button
                  type="button"
                  className="mt-5 h-10 rounded-lg border-app-action bg-app-action px-4 text-sm font-semibold text-app-action-foreground hover:bg-app-action-hover"
                  onClick={onCreate}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create app
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <DeleteAppDialog
        app={deleteTarget}
        deleting={Boolean(deletingAppId)}
        error={deleteError}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError('');
        }}
        onDelete={() => void deleteApp()}
      />
    </main>
  );
}
